import {
    AudioVideoFacade,
    ConsoleLogger,
    DefaultMeetingSession,
    VideoFxProcessor,
    VideoTransformDevice,
} from "amazon-chime-sdk-js";

export const queryKey = {
    backgroundImages: () => [],
};

export type Status = "loading" | "join" | "in-progress" | "completed";
export type ButtonSizeType = "xs" | "sm" | "md";
export type LayoutType = "tiled" | "sidebar";
export type VideoQuality = "360p" | "540p" | "720p";
export type RoomType = "main" | "breakout";
export type MeetingMessage = RoomClosedMessage | RoomOpenedMessage;
export const BREAKOUT = "breakout";

export const KEY_CODES = {
    TAB: 9,
    ENTER: 13,
    ESCAPE: 27,
    ARROW_UP: 38,
    ARROW_DOWN: 40,
};

export const VIDEO_INPUT_QUALITY = {
    "360p": "360p (nHD) @ 15 fps (600 Kbps max)",
    "540p": "540p (qHD) @ 15 fps (1.4 Mbps max)",
    "720p": "720p (HD) @ 15 fps (1.4 Mbps max)",
};

export const BUTTON_SIZE = {
    xs: { width: 24, height: 24 },
    sm: { width: 36, height: 36 },
    md: { width: 44, height: 44 },
    xl: { width: 64, height: 64 },
};

export enum DeviceLabels {
    None,
    Audio,
    Video,
    AudioAndVideo,
}

export interface State {
    title: string;
    meetingId: string;
    recorderId?: string;
    mainMeetingId: string;
    parentMeetingTitle: string;
    status: Status;
    isBreakoutRoom?: boolean;
    breakoutRooms: Room[];
    breakoutRoomTitle?: string;
    start_time?: string;
    end_time?: string;
    description?: string;
    phone_number?: string;

    layoutType: LayoutType;
    organizerId?: string;
    showMembers?: boolean;
    showInfo?: boolean;
    showNotes?: boolean;
    videoQuality: VideoQuality;

    currentUser: {
        attendeeId: string;
        isOrganizer?: boolean;
        canRaiseHand?: boolean;
        canCreateBreakoutRooms?: boolean;
        canTakeNotes?: boolean;
        canBlurBackground?: boolean;
        canMuteAll?: boolean;
        canUnmute?: boolean;
        canRecord?: boolean;
    };

    isMuted: boolean;
    isVideoEnabled: boolean;
    isEffectsSupported: boolean;
    isPreviewVideoEnabled: boolean;
    isRaised?: boolean;
    isMuteAll?: boolean;
    isOrganizer?: boolean;
    canUnmuteLocalAudio?: boolean;

    unmuteIds: string[];

    logger: ConsoleLogger;
    meetingSession?: DefaultMeetingSession;
    audioVideo?: AudioVideoFacade;
    audioInputDevices: MediaDeviceInfo[];
    audioOutputDevices: MediaDeviceInfo[];
    videoInputDevices: MediaDeviceInfo[];
    selectedAudioInputDevice?: string;
    selectedAudioOutputDevice?: string;
    selectedVideoInputDevice?: string;
    videoFxProcessor?: VideoFxProcessor;
    videoTransformDevice?: VideoTransformDevice;

    attendeeIds: string[];
    activeSpeakerIds: string[];
    screenShareIds: string[];
    roster: RosterType;
    tileIdToAttendeeId: { [id: number]: string };
    attendeeIdToTileId: { [id: string]: number };
    attendeeIdToVolume: { [id: string]: number };
    attendeeIdToMuted: { [id: string]: boolean };
    attendeeIdToSignalStrength: { [id: string]: number };
    attendeeIdToRaised: { [id: string]: boolean };

    recording: {
        isStarting?: boolean;
        startTime?: number;
        isRecording?: boolean;
    };

    chat: {
        isOpen: boolean;
        unreadCount: number;
        messages: ChatMessage[];
    };

    breakout: {
        rooms?: Room[];
        isFetching?: boolean;
        isLoading?: boolean;
        lastFetched?: number;
        autoJoin?: boolean;
        joinMeetingId?: string;
    };
}

export type RosterAttendeeType = {
    chimeAttendeeId: string;
    externalUserId?: string;
    name?: string;
    userId?: string;
    type?: string;
    avatar_url?: string;
};

export type RosterType = {
    [attendeeId: string]: RosterAttendeeType;
};

export interface ChatMessage {
    msg: string;
    sender: string;
    timestampMs: number;
}

export interface Recorder {
    AttendeeId?: string;
}

export interface RoomUser {
    id: string;
    display_name: string;
}

export interface MeetingUser {
    id: string;
    display_name: string;
    email?: string;
    system_user?: boolean;
    avatar_url?: string;
}

export interface Room {
    id: string;
    room_type: RoomType;
    meeting_id: string;
    title: string;
    is_open: boolean;
    users: RoomUser[];
    parent_meeting_id?: string;
}

export interface RoomOpenedMessage {
    action: "room:opened";
    data: Room;
}

export interface RoomClosedMessage {
    action: "room:closed";
    data: Room;
}

export interface MeetingResponseData {
    meeting_data: {
        Meeting: {
            MeetingId: string;
            MediaPlacement: {
                AudioHostUrl: string;
                AudioFallbackUrl: string;
                ScreenDataUrl: string;
                ScreenSharingUrl: string;
                ScreenViewingUrl: string;
                SignalingUrl: string;
                TurnControlUrl: string;
            };
        };
        Attendee: {
            AttendeeId: string;
            ExternalUserId: string;
            JoinToken: string;
        };
    };
    attendee: {
        id: string;
        name: string;
        avatar_url?: string;
    };
    organizer_id: string;
    can_mute_all: boolean;
    can_raise_hand: boolean;
    can_take_notes: boolean;
    can_record: boolean;
    can_create_breakout_rooms: boolean;
    can_blur_background: boolean;
    is_organizer: boolean;
    recorder?: {
        AttendeeId: string;
    };
    meeting_id: string;
    is_breakout_room: boolean;
    main_meeting_id: string;
    parent_meeting_title: string;
    breakout_room_title: string;
    my_breakout_rooms: Room[];
    title: string;
    start_time: string;
    end_time: string;
    phone_number: string;
    description: string;
}

export interface BackgroundImage {
    id: string;
    thumb: string;
    url: string;
    can_delete: boolean;
}

export interface UploadFormData {
    image: File;
}

export interface MeetingEvent {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    attributes: any;
}

export interface MeetingNoteFormData {
    note: string;
    user_id: string;
}
